/**
 * Journey Store - Manages journey framework and project progress state
 * Fetches journey data from API and provides computed values for Dashboard/UI
 */

import { create } from 'zustand'
import { journeyApi, type JourneyFrameworkResponse, type ProjectJourneyResponse } from '@/lib/api/client'
import type { JourneyFramework, JourneyPhase, JourneyGoal, PhaseMode, JourneyPhaseWithMode } from '@ispot/shared'

// ============================================================================
// TYPES
// ============================================================================

interface JourneyState {
  // Framework (from API)
  framework: JourneyFramework | null
  frameworkRecord: JourneyFrameworkResponse | null

  // Project journey progress (from API)
  journey: ProjectJourneyResponse | null

  // Loading states
  isLoading: boolean
  isUpdating: boolean
  error: string | null

  // Actions
  fetchFramework: (accountId: string) => Promise<void>
  fetchJourney: (accountId: string, projectId: string) => Promise<void>
  completeActivity: (accountId: string, projectId: string, activityId: string) => Promise<void>
  uncompleteActivity: (accountId: string, projectId: string, activityId: string) => Promise<void>
  completeArtifact: (accountId: string, projectId: string, artifactId: string) => Promise<void>
  selectGoal: (accountId: string, projectId: string, goalId: string) => Promise<void>
  advancePhase: (accountId: string, projectId: string, phaseId: string) => Promise<void>
  updateProgress: (accountId: string, projectId: string, progress: Record<string, number>) => Promise<void>

  // Computed getters (called as functions)
  getCurrentPhase: () => JourneyPhase | null
  getPhaseById: (phaseId: string) => JourneyPhase | null
  getSelectedGoal: () => JourneyGoal | null
  getPhaseProgress: (phaseId: string) => number
  getDomainScores: () => { market: number; solution: number; business: number }
  getOverallProgress: () => number
  isActivityCompleted: (activityId: string) => boolean
  isArtifactCompleted: (artifactId: string) => boolean

  // Goal-driven computed getters
  hasGoal: () => boolean
  getActivePhases: () => JourneyPhaseWithMode[]
  getPhaseMode: (phaseId: string) => PhaseMode
  isPhaseUnlocked: (phaseId: string) => boolean

  // Reset
  reset: () => void
}

// ============================================================================
// DEFAULT STATE
// ============================================================================

const defaultState = {
  framework: null as JourneyFramework | null,
  frameworkRecord: null as JourneyFrameworkResponse | null,
  journey: null as ProjectJourneyResponse | null,
  isLoading: false,
  isUpdating: false,
  error: null as string | null,
}

// ============================================================================
// STORE
// ============================================================================

export const useJourneyStore = create<JourneyState>()((set, get) => ({
  ...defaultState,

  // Fetch framework only (account-level, no project needed)
  fetchFramework: async (accountId: string) => {
    set({ isLoading: true, error: null })

    try {
      const { framework } = await journeyApi.getFramework(accountId)

      set({
        frameworkRecord: framework,
        framework: framework.config,
        isLoading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch framework',
        isLoading: false,
      })
    }
  },

  // Fetch journey framework and project progress
  fetchJourney: async (accountId: string, projectId: string) => {
    set({ isLoading: true, error: null })

    try {
      const { journey, framework } = await journeyApi.getProjectJourney(accountId, projectId)

      set({
        journey,
        frameworkRecord: framework,
        framework: framework.config,
        isLoading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch journey',
        isLoading: false,
      })
    }
  },

  // Complete an activity
  completeActivity: async (accountId: string, projectId: string, activityId: string) => {
    set({ isUpdating: true })

    try {
      const { journey } = await journeyApi.completeActivity(accountId, projectId, activityId)
      set({ journey, isUpdating: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to complete activity',
        isUpdating: false,
      })
    }
  },

  // Uncomplete an activity
  uncompleteActivity: async (accountId: string, projectId: string, activityId: string) => {
    set({ isUpdating: true })

    try {
      const { journey } = await journeyApi.uncompleteActivity(accountId, projectId, activityId)
      set({ journey, isUpdating: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to uncomplete activity',
        isUpdating: false,
      })
    }
  },

  // Complete an artifact
  completeArtifact: async (accountId: string, projectId: string, artifactId: string) => {
    set({ isUpdating: true })

    try {
      const { journey } = await journeyApi.completeArtifact(accountId, projectId, artifactId)
      set({ journey, isUpdating: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to complete artifact',
        isUpdating: false,
      })
    }
  },

  // Select a goal path
  selectGoal: async (accountId: string, projectId: string, goalId: string) => {
    set({ isUpdating: true })

    try {
      const { journey } = await journeyApi.selectGoal(accountId, projectId, goalId)
      set({ journey, isUpdating: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to select goal',
        isUpdating: false,
      })
    }
  },

  // Advance to next phase
  advancePhase: async (accountId: string, projectId: string, phaseId: string) => {
    set({ isUpdating: true })

    try {
      const { journey } = await journeyApi.advancePhase(accountId, projectId, phaseId)
      set({ journey, isUpdating: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to advance phase',
        isUpdating: false,
      })
    }
  },

  // Update phase progress
  updateProgress: async (accountId: string, projectId: string, progress: Record<string, number>) => {
    set({ isUpdating: true })

    try {
      const { journey } = await journeyApi.updateProgress(accountId, projectId, { phaseProgress: progress })
      set({ journey, isUpdating: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update progress',
        isUpdating: false,
      })
    }
  },

  // Computed: Get current phase
  getCurrentPhase: () => {
    const { framework, journey } = get()
    if (!framework || !journey) return null
    return framework.phases.find(p => p.id === journey.currentPhaseId) || null
  },

  // Computed: Get phase by ID
  getPhaseById: (phaseId: string) => {
    const { framework } = get()
    if (!framework) return null
    return framework.phases.find(p => p.id === phaseId) || null
  },

  // Computed: Get selected goal
  getSelectedGoal: () => {
    const { framework, journey } = get()
    if (!framework || !journey?.goalId) return null
    return framework.goals.find(g => g.id === journey.goalId) || null
  },

  // Computed: Get phase progress percentage
  getPhaseProgress: (phaseId: string) => {
    const { journey } = get()
    if (!journey) return 0
    return journey.phaseProgress[phaseId] || 0
  },

  // Computed: Get domain health scores
  getDomainScores: () => {
    const { framework, journey } = get()
    if (!framework || !journey) {
      return { market: 0, solution: 0, business: 0 }
    }

    // Calculate scores based on phase progress grouped by domain
    const domainProgress: Record<string, number[]> = {
      market: [],
      solution: [],
      business: [],
    }

    for (const phase of framework.phases) {
      const progress = journey.phaseProgress[phase.id] || 0
      if (domainProgress[phase.domain]) {
        domainProgress[phase.domain].push(progress)
      }
    }

    const average = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0

    return {
      market: Math.round(average(domainProgress.market)),
      solution: Math.round(average(domainProgress.solution)),
      business: Math.round(average(domainProgress.business)),
    }
  },

  // Computed: Overall progress (over active phases only, so Iterate doesn't dilute the %)
  getOverallProgress: () => {
    const { journey } = get()
    if (!journey) return 0

    const activePhases = get().getActivePhases()
    if (activePhases.length === 0) return 0

    const total = activePhases.reduce((sum, phase) => {
      return sum + (journey.phaseProgress[phase.id] || 0)
    }, 0)

    return Math.round(total / activePhases.length)
  },

  // Computed: Check if activity is completed
  isActivityCompleted: (activityId: string) => {
    const { journey } = get()
    if (!journey) return false
    return journey.completedActivities.includes(activityId)
  },

  // Computed: Check if artifact is completed
  isArtifactCompleted: (artifactId: string) => {
    const { journey } = get()
    if (!journey) return false
    return journey.completedArtifacts.includes(artifactId)
  },

  // Computed: Check if a goal is selected
  hasGoal: () => {
    const { journey } = get()
    return !!journey?.goalId
  },

  // Computed: Get phases filtered by selected goal with their modes.
  // Iterate (id: 'iterate') is always excluded — it only makes sense post-launch
  // and its presence in the denominator inflates / deflates progress %s.
  getActivePhases: () => {
    const { framework, journey } = get()
    if (!framework) return []

    const buildable = framework.phases.filter(phase => phase.id !== 'iterate')

    const goal = journey?.goalId
      ? framework.goals.find(g => g.id === journey.goalId)
      : null

    if (!goal) {
      return buildable
        .filter(phase => phase.type !== 'iteration')
        .map(phase => ({ ...phase, mode: 'full' as PhaseMode }))
        .sort((a, b) => a.order - b.order)
    }

    return buildable
      .filter(phase => goal.phaseConfig[phase.id] !== 'skip')
      .map(phase => ({
        ...phase,
        mode: (goal.phaseConfig[phase.id] || 'full') as PhaseMode,
      }))
      .sort((a, b) => a.order - b.order)
  },

  // Computed: Get the mode for a specific phase
  getPhaseMode: (phaseId: string) => {
    const { framework, journey } = get()
    if (!framework || !journey?.goalId) return 'full' as PhaseMode

    const goal = framework.goals.find(g => g.id === journey.goalId)
    if (!goal) return 'full' as PhaseMode

    return (goal.phaseConfig[phaseId] || 'full') as PhaseMode
  },

  // Computed: Check if a phase is unlocked (previous phases completed)
  isPhaseUnlocked: (phaseId: string) => {
    const { framework, journey } = get()
    if (!framework || !journey) return false

    const activePhases = get().getActivePhases()
    const phaseIndex = activePhases.findIndex(p => p.id === phaseId)

    // First phase is always unlocked
    if (phaseIndex <= 0) return true

    // Check if previous phase has enough progress (>= 60% is considered unlocked)
    const previousPhase = activePhases[phaseIndex - 1]
    const previousProgress = journey.phaseProgress[previousPhase.id] || 0
    return previousProgress >= 60
  },

  // Reset state
  reset: () => set(defaultState),
}))

export default useJourneyStore
